'use client'; // Assuming Next.js App Router context if this is a page/component
import QRCode from "qrcode.react";
import { useRef, useState, useEffect } from "react";
import { FaDownload, FaQrcode } from "react-icons/fa"; // Added icons

const MainQrGenerator = () => { // Renamed for clarity if it's a specific component
    const [inputValue, setInputValue] = useState(""); // More descriptive name
    const [qrValue, setQrValue] = useState("https://example.com"); // Default QR value for initial display
    const qrCodeRef = useRef(null);

    // Update QR code only when inputValue is not empty, otherwise show default
    useEffect(() => {
        if (inputValue.trim() !== "") {
            setQrValue(inputValue);
        } else {
            // Optionally, set to a default placeholder or clear it
            setQrValue("Enter URL or text above"); // Or a generic QR like your site's URL
        }
    }, [inputValue]);

    const handleInputChange = (e) => {
        setInputValue(e.target.value);
    };

    const downloadQRCode = () => {
        if (!qrCodeRef.current || inputValue.trim() === "") return; // Don't download if empty

        const canvas = qrCodeRef.current.querySelector('canvas');
        if (!canvas) {
            console.error("Canvas element not found for QR code.");
            return;
        }

        const pngUrl = canvas.toDataURL('image/png');
        const downloadLink = document.createElement('a');
        downloadLink.href = pngUrl;
        // Generate a more dynamic filename, e.g., based on the input
        const filename = inputValue.trim().replace(/[^a-z0-9]/gi, '_').toLowerCase() || 'qr-code';
        downloadLink.download = `${filename.substring(0, 20)}_qr.png`; // Limit filename length

        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
      };

    return (
        // Full screen, centered content using flexbox
        <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-slate-900 via-blue-900 to-slate-800 p-4 font-montserrat text-gray-100">
            <div className="bg-gray-800 shadow-2xl rounded-xl p-6 sm:p-10 w-full max-w-md transform transition-all hover:scale-[1.01] duration-300">
                <div className="text-center mb-8">
                    <FaQrcode className="mx-auto text-5xl text-blue-400 mb-3" />
                    <h2 className="text-3xl font-poppins font-semibold text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400">
                        QR Code Generator
                    </h2>
                    <p className="text-gray-400 mt-1 text-sm">Instantly create and download your QR code.</p>
                </div>

                <div className="space-y-6">
                    <div>
                        <label htmlFor="qr-input" className="block text-sm font-medium text-gray-300 mb-1">
                            Enter URL or Text:
                        </label>
                        <input
                            id="qr-input"
                            type="text"
                            value={inputValue}
                            onChange={handleInputChange}
                            placeholder="e.g., https://mywebsite.com"
                            className="w-full px-4 py-2.5 bg-gray-700 border border-gray-600 rounded-lg text-gray-100 placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-shadow"
                        />
                    </div>

                    {/* QR Code Display */}
                    <div className="flex justify-center p-4 bg-white rounded-lg shadow-inner" ref={qrCodeRef}>
                        {/* The 'key' prop helps React re-render QRCode when value changes significantly if needed,
                            but qrcode.react usually handles updates well internally based on 'value' prop.
                            Forcing a re-render can be useful if it ever gets "stuck".
                            Using qrValue ensures it always has *something* to render.
                        */}
                        <QRCode
                            value={qrValue}
                            size={200} // Adjust size as needed
                            level="H" // Error correction level (L, M, Q, H)
                            includeMargin={true}
                            bgColor="#FFFFFF"
                            fgColor="#0D1117" // Dark foreground for good contrast on white
                        />
                    </div>

                    <button
                        onClick={downloadQRCode}
                        disabled={!inputValue.trim()} // Disable if input is empty
                        className="w-full flex items-center justify-center px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-800 disabled:bg-gray-600 disabled:cursor-not-allowed disabled:opacity-70"
                    >
                        <FaDownload className="mr-2" />
                        Download QR Code
                    </button>
                </div>
            </div>

            <footer className="mt-10 text-center text-xs text-gray-500">
                <p>© {new Date().getFullYear()} QR Generator. All rights reserved.</p>
                <p className="mt-1">
                    Powered by <a href="https://reactjs.org/" target="_blank" rel="noopener noreferrer" className="hover:text-blue-400">React</a> &
                    <a href="https://tailwindcss.com/" target="_blank" rel="noopener noreferrer" className="hover:text-blue-400"> Tailwind CSS</a>.
                </p>
            </footer>
        </div>
    );
}

export default MainQrGenerator;